import { Injectable } from '@angular/core';
import { CityParkingRegisterGroupResponse } from '../../models/register-parking';
import {
  RegisterParkingChartItem,
  RegisterParkingDashboardStat,
  RegisterParkingMarketingDashboardDto,
  RegisterParkingMarketingDashboardView,
  RegisterParkingRecentActivityItem,
  RegisterParkingSummary,
  RegisterParkingTableDto,
} from '../../models/parking/register-parking-list.model';

@Injectable({
  providedIn: 'root',
})
export class MapperRegisterParkingListService {
  mapTableFromDto(dto: RegisterParkingTableDto): {
    items: CityParkingRegisterGroupResponse[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as RegisterParkingTableDto & Record<string, unknown>;
    return {
      items: ((dto.data ?? raw['Data'] ?? []) as CityParkingRegisterGroupResponse[]).map(item =>
        this.normalizeItem(item)
      ),
      total: dto.total ?? (raw['Total'] as number) ?? 0,
      page: dto.page ?? (raw['Page'] as number) ?? 1,
      pageSize: dto.pageSize ?? (raw['PageSize'] as number) ?? 20,
      totalPages: dto.totalPages ?? (raw['TotalPages'] as number) ?? 0,
    };
  }

  mapMarketingDashboard(dto: RegisterParkingMarketingDashboardDto): RegisterParkingMarketingDashboardView {
    const raw = dto as RegisterParkingMarketingDashboardDto & Record<string, unknown>;
    const summaryRaw = dto.summary ?? raw['Summary'];
    const ratiosRaw = dto.ratios ?? raw['Ratios'];
    const recentRaw = (dto.recentActivity ?? raw['RecentActivity'] ?? []) as RegisterParkingRecentActivityItem[];
    const chartRaw = (dto.chartItems ?? raw['ChartItems'] ?? []) as RegisterParkingChartItem[];

    const summary = this.normalizeSummary(summaryRaw as RegisterParkingSummary | Record<string, unknown>);
    const ratiosAny = ratiosRaw as unknown as Record<string, number> | undefined;

    return {
      stats: this.mapDashboardStats(summary),
      ratios: {
        confirmedPercent:
          ratiosRaw?.confirmedPercent ?? ratiosAny?.['ConfirmedPercent'] ?? 0,
        draftPercent: ratiosRaw?.draftPercent ?? ratiosAny?.['DraftPercent'] ?? 0,
      },
      recentActivity: recentRaw.map(item => this.mapRecentActivityItem(item)),
      chartItems: chartRaw.map(item => this.normalizeChartItem(item)),
    };
  }

  mapSearchField(type: 'customerName' | 'name' | 'invoiceNo' | 'description' | 'clientName'): string {
    switch (type) {
      case 'name':
        return 'name';
      case 'invoiceNo':
        return 'invoiceno';
      case 'description':
        return 'description';
      case 'clientName':
        return 'client';
      default:
        return 'customername';
    }
  }

  mapStatusFilter(filterStatus: number): 'all' | 'draft' | 'confirmed' | 'cancelled' | undefined {
    switch (filterStatus) {
      case 1:
        return 'draft';
      case 2:
        return 'confirmed';
      case 3:
        return 'cancelled';
      default:
        return undefined;
    }
  }

  private mapDashboardStats(summary: RegisterParkingSummary): RegisterParkingDashboardStat[] {
    return [
      {
        title: 'ข้อมูลรวมทั้งหมด',
        subtitle: '',
        value: (summary.totalCount || 0).toLocaleString(),
        icon: 'folder',
        color: 'text-blue-600',
      },
      {
        title: 'ลงทะเบียนใหม่ (7 วัน)',
        subtitle: 'สถานะที่ยืนยันแล้ว',
        value: (summary.newPaid7Days || 0).toLocaleString(),
        icon: 'event_available',
        color: 'text-emerald-500',
      },
      {
        title: 'ยอดค่าธรรมเนียมรวม',
        subtitle: '',
        value: (summary.totalFeePaid || 0).toLocaleString('th-TH') + ' ฿',
        icon: 'payments',
        color: 'text-amber-500',
      },
      {
        title: 'ฉบับร่าง',
        subtitle: '',
        value: (summary.draftCount || 0).toLocaleString(),
        icon: 'edit_note',
        color: 'text-slate-500',
      },
    ];
  }

  private normalizeItem(item: CityParkingRegisterGroupResponse): CityParkingRegisterGroupResponse {
    const raw = item as CityParkingRegisterGroupResponse & Record<string, unknown>;
    return {
      id: item.id ?? (raw['Id'] as number) ?? 0,
      name: item.name ?? (raw['Name'] as string) ?? '',
      description: item.description ?? (raw['Description'] as string) ?? '',
      cpCustomerId: item.cpCustomerId ?? (raw['CpCustomerId'] as number) ?? 0,
      customerName: item.customerName ?? (raw['CustomerName'] as string) ?? '',
      invoiceNo: item.invoiceNo ?? (raw['InvoiceNo'] as string | null) ?? null,
      fee: item.fee ?? (raw['Fee'] as number | null) ?? null,
      createDate: item.createDate ?? (raw['CreateDate'] as string | null) ?? null,
      carCount: item.carCount ?? (raw['CarCount'] as number) ?? 0,
      feeName: item.feeName ?? (raw['FeeName'] as string) ?? '',
      status: item.status ?? (raw['Status'] as number) ?? 0,
      clientId: item.clientId ?? (raw['ClientId'] as number | null) ?? null,
      clientName: item.clientName ?? (raw['ClientName'] as string | null) ?? null,
    };
  }

  private normalizeSummary(input: RegisterParkingSummary | Record<string, unknown> | null | undefined): RegisterParkingSummary {
    const raw = (input ?? {}) as RegisterParkingSummary & Record<string, unknown>;
    return {
      totalCount: raw.totalCount ?? (raw['TotalCount'] as number) ?? 0,
      newPaid7Days: raw.newPaid7Days ?? (raw['NewPaid7Days'] as number) ?? 0,
      totalFeePaid: raw.totalFeePaid ?? (raw['TotalFeePaid'] as number) ?? 0,
      draftCount: raw.draftCount ?? (raw['DraftCount'] as number) ?? 0,
    };
  }

  private mapRecentActivityItem(item: RegisterParkingRecentActivityItem): RegisterParkingRecentActivityItem {
    const raw = item as RegisterParkingRecentActivityItem & Record<string, unknown>;
    return {
      id: item.id ?? (raw['Id'] as number) ?? 0,
      customerName: item.customerName ?? (raw['CustomerName'] as string) ?? '',
      name: item.name ?? (raw['Name'] as string) ?? '',
      fee: item.fee ?? (raw['Fee'] as number | null) ?? null,
      createDate: item.createDate ?? (raw['CreateDate'] as string | null) ?? null,
    };
  }

  private normalizeChartItem(item: RegisterParkingChartItem): RegisterParkingChartItem {
    const raw = item as RegisterParkingChartItem & Record<string, unknown>;
    return {
      id: item.id ?? (raw['Id'] as number) ?? 0,
      customerName: item.customerName ?? (raw['CustomerName'] as string) ?? '',
      name: item.name ?? (raw['Name'] as string) ?? '',
      createDate: item.createDate ?? (raw['CreateDate'] as string | null) ?? null,
      fee: item.fee ?? (raw['Fee'] as number | null) ?? null,
    };
  }
}
